/**
 * The human solver: apply the easiest available technique, repeat, and record
 * every deduction on the way.
 *
 * This is the engine both halves of the product are built on. `grade.ts` reads
 * the returned steps to rate a puzzle; the hint UI reads the *first* step of a
 * fresh call to teach the player their next move. They must never diverge, so
 * neither of them re-implements any part of this.
 *
 * It solves by logic only -- there is no guessing anywhere in this file. A
 * puzzle it cannot finish is one that needs a technique we have not written
 * yet, which is exactly what `grade` reports.
 */

import { CELLS, EMPTY, cloneBoard, isComplete, type Board } from "./grid";
import { computeCandidates, maskOf, type Candidates } from "./candidates";
import { CLASSIC, type ConstraintSet } from "./constraints";
import { TECHNIQUES } from "./techniques";
import type { Step, Technique } from "./techniques/types";

export interface HumanSolveOptions {
  cs?: ConstraintSet;
  techniques?: readonly Technique[];
  /** Safety valve. A step must always change something, so this never trips. */
  maxSteps?: number;
  /**
   * Called with each deduction as it is applied. Used by the tests to check
   * every step against the real solution, and by the UI to replay a solve.
   */
  onStep?: (step: Step) => void;
}

export interface HumanSolveResult {
  readonly solved: boolean;
  /** How far logic got. Equal to the input if no technique applied. */
  readonly board: Board;
  readonly steps: readonly Step[];
  /**
   * A cell ran out of candidates. Impossible for a proper puzzle, so this
   * means the input was over-constrained or a technique is buggy.
   */
  readonly contradiction: boolean;
}

function applyStep(step: Step, board: Board, candidates: Candidates, cs: ConstraintSet): void {
  for (const { cell, digit } of step.placements) {
    board[cell] = digit;
    candidates[cell] = 0;
    const bit = maskOf(digit);
    for (const peer of cs.peers[cell]!) candidates[peer]! &= ~bit;
  }
  for (const { cell, digit } of step.eliminations) {
    candidates[cell]! &= ~maskOf(digit);
  }
}

function isStuck(board: Board, candidates: Candidates): boolean {
  for (let cell = 0; cell < CELLS; cell++) {
    if (board[cell] === EMPTY && candidates[cell] === 0) return true;
  }
  return false;
}

export function solveHuman(input: Board, options: HumanSolveOptions = {}): HumanSolveResult {
  const cs = options.cs ?? CLASSIC;
  const techniques = options.techniques ?? TECHNIQUES;
  const maxSteps = options.maxSteps ?? 1000;

  const board = cloneBoard(input);
  // Candidates are carried across the whole solve rather than recomputed:
  // eliminations from subsets and fish are not implied by the placed digits, so
  // recomputing would silently throw away most of the work.
  const candidates = computeCandidates(board, cs);
  const steps: Step[] = [];

  while (steps.length < maxSteps && !isComplete(board)) {
    if (isStuck(board, candidates)) {
      return { solved: false, board, steps, contradiction: true };
    }

    let step: Step | null = null;
    for (const technique of techniques) {
      step = technique.find({ board, candidates, cs });
      if (step) break;
    }
    if (!step) break;

    applyStep(step, board, candidates, cs);
    steps.push(step);
    options.onStep?.(step);
  }

  return { solved: isComplete(board), board, steps, contradiction: false };
}

/**
 * The single next deduction, for the hint system.
 *
 * Deliberately derived from the true candidates rather than the player's pencil
 * marks: a hint should teach the move that is actually available, not repeat
 * the player's bookkeeping mistakes.
 */
export function nextStep(board: Board, options: HumanSolveOptions = {}): Step | null {
  const cs = options.cs ?? CLASSIC;
  const techniques = options.techniques ?? TECHNIQUES;
  const candidates = computeCandidates(board, cs);

  for (const technique of techniques) {
    const step = technique.find({ board, candidates, cs });
    if (step) return step;
  }
  return null;
}
